import { useState, useEffect } from 'react'
import { Search, X } from 'lucide-react'
import { cn } from '@frontend/lib/utils'

interface SearchFilterProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  /** Debounce delay in milliseconds (default: 300) */
  debounceMs?: number
}

/**
 * Search input with debouncing for filtering.
 * Updates the parent after a delay to avoid excessive re-renders.
 */
export function SearchFilter({
  value,
  onChange,
  placeholder = 'Search...',
  className,
  debounceMs = 300,
}: SearchFilterProps) {
  const [localValue, setLocalValue] = useState(value)

  // Sync local value when parent value changes
  useEffect(() => {
    setLocalValue(value)
  }, [value])

  // Debounce updates to parent
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localValue !== value) {
        onChange(localValue)
      }
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [localValue, value, onChange, debounceMs])

  const handleClear = () => {
    setLocalValue('')
    onChange('')
  }

  return (
    <div className={cn('relative', className)}>
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        type="text"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        placeholder={placeholder}
        className={cn(
          'h-9 w-64 rounded-md border border-input bg-background pl-9 pr-8 text-sm',
          'placeholder:text-muted-foreground',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
        )}
      />
      {localValue && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}
